/**
 * Step 4 Mobile: Property
 * Property cards with expandable mortgage/sale/rental sections.
 */

import { useForm, useFieldArray } from 'react-hook-form';
import type { Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type Step4FormData, Step4Schema, type PropertyItem, type AllStepFormData } from '../../types/wizard';
import { WizardMobileShell } from './WizardMobileShell';
import { useState } from 'react';

interface Step4Props {
  initialData?: Partial<AllStepFormData>;
  onNext: (data: Step4FormData) => void;
  onSave: (data: Step4FormData) => void;
  onBack?: () => void;
  lastSaved?: string;
}

const propertyTypes: Record<string, string> = {
  PRIMARY_RESIDENCE: 'Primary Residence',
  BUY_TO_LET: 'Buy to Let',
  HOLIDAY_LET: 'Holiday Let',
};

export default function Step4PropertyMobile({ initialData, onNext, onSave, onBack, lastSaved }: Readonly<Step4Props>) {
  const { register, handleSubmit, watch, setValue, control } = useForm<Step4FormData>({
    resolver: zodResolver(Step4Schema),
    defaultValues: {
      includeProperty: initialData?.includeProperty ?? false,
      properties: initialData?.properties || [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'properties' });
  const [expanded, setExpanded] = useState<Record<number, { mortgage: boolean; sale: boolean; rental: boolean }>>({});

  const toggle = (index: number, section: 'mortgage' | 'sale' | 'rental') => {
    setExpanded(prev => ({
      ...prev,
      [index]: { ...prev[index], [section]: !prev[index]?.[section] },
    }));
  };

  const handleBlur = () => onSave(watch());

  const addProperty = () => {
    // Type assertion needed: useFieldArray append requires full PropertyItem but we provide safe defaults
    append({
      propertyId: crypto.randomUUID(),
      label: '',
      propertyType: 'PRIMARY_RESIDENCE',
      owner: 'person0',
      currentMarketValue: 0,
      currentMortgageAmount: 0,
      currentEquity: 0,
      estateAgentFeePct: 1.5,
      planningSale: false,
      newPropertyPurchasePrice: 0,
      proceedsStrategy: 'INVEST',
      monthlyMortgagePayment: 0,
      purchasePrice: 0,
      acquisitionCosts: 0,
      improvementCosts: 0,
      interestOnlyMortgageAmount: 0,
      mortgageInterestRatePct: 0,
      legalFees: 0,
      rental: { annualGrossIncome: 0, annualAllowableExpenses: 0, occupancyRatePct: 90 },
    } as PropertyItem);
  };

  return (
    <WizardMobileShell step={4} totalSteps={10} title="Property" onBack={onBack} onContinue={() => handleSubmit(onNext)()} lastSaved={lastSaved}>
      <div className="mobile-step-form">
        <label className="auth-mobile__checkbox" style={{ marginBottom: 16 }}>
          <input
            type="checkbox"
            {...register('includeProperty')}
            onChange={e => { void register('includeProperty').onChange(e); handleBlur(); }}
          />
          <span>I own property</span>
        </label>

        {fields.map((field, i) => {
          const v = watch('properties')?.[i];
          return (
            <div key={field.id} className="mobile-person-card">
              <h4 className="mobile-person-card__title">{v?.label || 'Property'}</h4>

              <label className="mobile-label">Property name</label>
              <input type="text" className="mobile-input" placeholder="e.g. Our home, Flat in Leeds" {...register(`properties.${i}.label` as Path<Step4FormData>)} onBlur={handleBlur} />

              <label className="mobile-label">Type</label>
              <div className="mobile-segmented-control">
                {Object.entries(propertyTypes).map(([k, label]) => (
                  <label key={k} className={`mobile-segment${(v?.propertyType || 'PRIMARY_RESIDENCE') === k ? ' mobile-segment--active' : ''}`}>
                    <input type="radio" className="mobile-segment-input" value={k} {...register(`properties.${i}.propertyType` as Path<Step4FormData>)} onChange={e => { void register(`properties.${i}.propertyType` as Path<Step4FormData>).onChange(e); handleBlur(); }} />
                    <span>{label === 'Primary Residence' ? 'Home' : label}</span>
                  </label>
                ))}
              </div>

              <label className="mobile-label">Market Value (£)</label>
              <input type="number" className="mobile-input" {...register(`properties.${i}.currentMarketValue` as Path<Step4FormData>, { valueAsNumber: true })} onBlur={() => {
                const mv = watch('properties')?.[i]?.currentMarketValue ?? 0;
                const ma = watch('properties')?.[i]?.currentMortgageAmount ?? 0;
                setValue(`properties.${i}.currentEquity` as Path<Step4FormData>, mv - ma);
                handleBlur();
              }} />

              <label className="mobile-label">Mortgage (£)</label>
              <input type="number" className="mobile-input" {...register(`properties.${i}.currentMortgageAmount` as Path<Step4FormData>, { valueAsNumber: true })} onBlur={() => {
                const mv = watch('properties')?.[i]?.currentMarketValue ?? 0;
                const ma = watch('properties')?.[i]?.currentMortgageAmount ?? 0;
                setValue(`properties.${i}.currentEquity` as Path<Step4FormData>, mv - ma);
                handleBlur();
              }} />
              <div className="mobile-equity-row">
                <span className="mobile-equity-label">Equity</span>
                <span className="mobile-equity-value">£{((v?.currentMarketValue || 0) - (v?.currentMortgageAmount || 0)).toLocaleString()}</span>
              </div>

              <button type="button" className="mobile-expand-toggle" onClick={() => toggle(i, 'mortgage')}>
                {expanded[i]?.mortgage ? '▾' : '▸'} Mortgage details
              </button>
              {expanded[i]?.mortgage && (
                <div className="mobile-expand-section">
                  <label className="mobile-label">Monthly payment (£)</label>
                  <input type="number" className="mobile-input" {...register(`properties.${i}.monthlyMortgagePayment` as Path<Step4FormData>, { valueAsNumber: true })} onBlur={handleBlur} />
                  <label className="mobile-label">Interest rate (%)</label>
                  <input type="number" className="mobile-input" step="0.1" {...register(`properties.${i}.mortgageInterestRatePct` as Path<Step4FormData>, { valueAsNumber: true })} onBlur={handleBlur} />
                  <label className="mobile-label">Interest-only portion (£)</label>
                  <input type="number" className="mobile-input" {...register(`properties.${i}.interestOnlyMortgageAmount` as Path<Step4FormData>, { valueAsNumber: true })} onBlur={handleBlur} />
                  <span className="mobile-hint">Balance never reduces — owed in full at the end date</span>
                  <label className="mobile-label">Repayment portion (£)</label>
                  <input
                    type="number"
                    className="mobile-input"
                    value={Math.max(0, (v?.currentMortgageAmount || 0) - (v?.interestOnlyMortgageAmount || 0))}
                    onChange={(e) => {
                      const newRepayment = Math.max(0, Number.parseFloat(e.target.value) || 0);
                      setValue(`properties.${i}.currentMortgageAmount` as Path<Step4FormData>, (v?.interestOnlyMortgageAmount || 0) + newRepayment);
                      handleBlur();
                    }}
                  />
                  <span className="mobile-hint">Reduces to £0 by the end date — editing this adjusts the total mortgage above</span>
                  <label className="mobile-label">End date</label>
                  <input type="date" className="mobile-input" {...register(`properties.${i}.mortgageEndDate` as Path<Step4FormData>)} onBlur={handleBlur} />
                </div>
              )}

              <button type="button" className="mobile-expand-toggle" onClick={() => toggle(i, 'sale')}>
                {expanded[i]?.sale ? '▾' : '▸'} Sale planning
              </button>
              {expanded[i]?.sale && (
                <div className="mobile-expand-section">
                  <label className="auth-mobile__checkbox">
                    <input type="checkbox"
                      {...register(`properties.${i}.planningSale` as Path<Step4FormData>)}
                      onChange={e => {
                        void register(`properties.${i}.planningSale` as Path<Step4FormData>).onChange(e);
                        handleBlur();
                      }}
                    />
                    <span>Planning to sell this property</span>
                  </label>
                  {watch('properties')?.[i]?.planningSale && (
                    <>
                      <label className="mobile-label">Planned sale date</label>
                      <input type="date" className="mobile-input" {...register(`properties.${i}.plannedSaleDate` as Path<Step4FormData>)} onBlur={handleBlur} />
                      <label className="mobile-label">New purchase price (£)</label>
                      <input type="number" className="mobile-input" {...register(`properties.${i}.newPropertyPurchasePrice` as Path<Step4FormData>, { valueAsNumber: true })} onBlur={handleBlur} />
                      <label className="mobile-label">Sale proceeds</label>
                      <div className="mobile-segmented-control">
                        {([['INVEST', 'Invest'], ['CASH', 'Keep as cash']] as const).map(([val, lbl]) => (
                          <label key={val} className={`mobile-segment${(watch('properties')?.[i]?.proceedsStrategy ?? 'INVEST') === val ? ' mobile-segment--active' : ''}`}>
                            <input type="radio" className="mobile-segment-input" value={val}
                              {...register(`properties.${i}.proceedsStrategy` as Path<Step4FormData>)}
                              onChange={e => { void register(`properties.${i}.proceedsStrategy` as Path<Step4FormData>).onChange(e); handleBlur(); }}
                            />
                            <span>{lbl}</span>
                          </label>
                        ))}
                      </div>
                      {(v?.propertyType === 'BUY_TO_LET' || v?.propertyType === 'HOLIDAY_LET') && (
                        <>
                          <label className="mobile-label">Purchase price (£) <span className="mobile-hint-inline">for CGT</span></label>
                          <input type="number" className="mobile-input"
                            {...register(`properties.${i}.purchasePrice` as Path<Step4FormData>, { valueAsNumber: true })}
                            onBlur={handleBlur}
                          />
                          <label className="mobile-label">Purchase costs (£)</label>
                          <input type="number" className="mobile-input"
                            {...register(`properties.${i}.acquisitionCosts` as Path<Step4FormData>, { valueAsNumber: true })}
                            onBlur={handleBlur}
                          />
                          <span className="mobile-hint">Stamp duty (SDLT), your solicitor's conveyancing fees and a survey you commissioned. Not mortgage arrangement fees or the lender's valuation: those are costs of the loan, which HMRC does not allow.</span>
                          <label className="mobile-label">Improvement costs (£)</label>
                          <input type="number" className="mobile-input"
                            {...register(`properties.${i}.improvementCosts` as Path<Step4FormData>, { valueAsNumber: true })}
                            onBlur={handleBlur}
                          />
                          <label className="mobile-label">Legal fees on sale (£)</label>
                          <input type="number" className="mobile-input"
                            {...register(`properties.${i}.legalFees` as Path<Step4FormData>, { valueAsNumber: true })}
                            onBlur={handleBlur}
                          />
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              <button type="button" className="mobile-expand-toggle" onClick={() => toggle(i, 'rental')}>
                {expanded[i]?.rental ? '▾' : '▸'} Rental income
              </button>
              {expanded[i]?.rental && (
                <div className="mobile-expand-section">
                  <label className="mobile-label">Gross income (£/year)</label>
                  <input type="number" className="mobile-input" {...register(`properties.${i}.rental.annualGrossIncome` as Path<Step4FormData>, { valueAsNumber: true })} onBlur={handleBlur} />
                  <label className="mobile-label">Allowable expenses (£/year)</label>
                  <input type="number" className="mobile-input"
                    {...register(`properties.${i}.rental.annualAllowableExpenses` as Path<Step4FormData>, { valueAsNumber: true })}
                    onBlur={handleBlur}
                  />
                  <label className="mobile-label">Occupancy rate (%)</label>
                  <input type="number" className="mobile-input" min={0} max={100}
                    {...register(`properties.${i}.rental.occupancyRatePct` as Path<Step4FormData>, { valueAsNumber: true })}
                    onBlur={handleBlur}
                  />
                </div>
              )}

              <button type="button" className="mobile-holding-card__delete" onClick={() => remove(i)}>✕ Remove</button>
            </div>
          );
        })}

        <button type="button" className="mobile-btn mobile-btn--outline mobile-btn--full" onClick={addProperty}>+ Add Property</button>
      </div>
    </WizardMobileShell>
  );
}
